import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import { ListHookOption } from "app/pages/sales-intelligence/modules/lists/components/ListHookOption/ListHookOption";
import ScrollableDropdownContainer from "app/pages/workspace/sales/components/custom-dropdown/ScrollableDropdownContainer/ScrollableDropdownContainer";

import type { AddOpportunitiesToListDto, OpportunityListType } from "../../../types";
import { OpportunityListTypeEnum } from "../../../types";

import AddToListDropdownItem from "./AddToListDropdownItem";
import CreateListForm from "./CreateListForm";
import CreateListItem from "./CreateListItem";
import {
    SCROLL_AREA_STYLES,
    StyledAddToListDropdown,
    StyledAddToListDropdownButton,
    StyledAddToListDropdownContent,
} from "./styles";
import type { AddToListDropdownProps, DisabledOpportunityListType } from "./types";

const AddToListDropdown = ({
    renderDropdownButton,
    updateListOpportunitiesFromSearch,
    createList,
    dropdownItemClick,
    setIsOpen,
    domains,
    listCreating,
    listUpdating,
    opportunityLists,
    className = "add-to-list-dropdown",
    labelCreateBtn,
    isOpen,
    disabledText,
    isDisabled,
}: AddToListDropdownProps) => {
    const [isCreateModeActive, setIsCreateModeActive] = useState(false);

    const closeDropdown = () => setIsOpen(false);
    const toggleDropdown = () => setIsOpen((prev) => !prev);

    useEffect(() => {
        if (!isOpen && isCreateModeActive) {
            setIsCreateModeActive(false);
        }
    }, [isOpen]);

    const handleItemClick = (list: DisabledOpportunityListType) => {
        if (list.disabled) {
            return;
        }

        const dto: AddOpportunitiesToListDto = {
            opportunities: domains,
        };

        if (typeof dropdownItemClick === "function") {
            dropdownItemClick(list, dto); // TODO add props items;
            setIsOpen(false);

            return;
        }

        updateListOpportunitiesFromSearch(list, dto);
    };

    const handleCreateCancelClick = () => setIsCreateModeActive(false);

    const handleCreateSubmit = (name: OpportunityListType["friendlyName"]) => {
        createList(name, domains);
    };

    const handleOutsideClick = () => {
        setIsCreateModeActive(false);
        closeDropdown();
    };

    useActionAfterFlagChange(listUpdating, () => {
        handleOutsideClick();
    });

    useDeactivateOnOutsideClick(className, isOpen, handleOutsideClick);

    function renderButton() {
        if (typeof renderDropdownButton === "function") {
            return renderDropdownButton(toggleDropdown);
        }

        return (
            <StyledAddToListDropdownButton
                onClick={toggleDropdown}
                data-automation="add-to-list-dropdown-button"
            >
                <I18n
                    data-automation="add-to-list-button.text"
                    translatable={{ key: "si.components.add_to_list_dropdown.button.text" }}
                />
                <SWReactIcons iconName="arrow" />
            </StyledAddToListDropdownButton>
        );
    }

    function renderContent() {
        if (isCreateModeActive) {
            return (
                <CreateListForm
                    loading={listCreating || listUpdating}
                    onSubmit={handleCreateSubmit}
                    onCancel={handleCreateCancelClick}
                    labelCreateBtn={labelCreateBtn}
                />
            );
        }

        const renderItem = (list: DisabledOpportunityListType): ReactNode => {
            if (list.listType === OpportunityListTypeEnum.RecentlyViewed) {
                return null as ReactNode;
            }
            const item = (
                <AddToListDropdownItem
                    key={list?.opportunityListId}
                    disabled={list.disabled}
                    list={list}
                    onClick={handleItemClick}
                    iconName={list.listType === OpportunityListTypeEnum.Shared ? "assign-to-user" : "list-icon"}
                />
            );

            if (disabledText && list.disabled) {
                return (
                    <PlainTooltip
                        maxWidth={200}
                        key={list.opportunityListId}
                        placement="top"
                        tooltipContent={disabledText}
                    >
                        <div>{item}</div>
                    </PlainTooltip>
                );
            }

            return item;
        };

        return (
            <Box padding="8px">
                <ScrollableDropdownContainer scrollAreaStyle={SCROLL_AREA_STYLES}>
                    {opportunityLists.map(renderItem)}
                </ScrollableDropdownContainer>
                {isDisabled ? (
                    <ListHookOption
                        title="si.components.add_to_list_dropdown.hook.title"
                        description="si.components.add_to_list_dropdown.hook.description"
                        buttonText="si.components.add_to_list_dropdown.hook.button"
                    />
                ) : null}
            </Box>
        );
    }

    return (
        <StyledAddToListDropdown
            className={className}
            data-automation="si.add-website-to-static-list-dropdown"
        >
            {renderButton()}
            <StyledAddToListDropdownContent open={isOpen}>
                <CreateListItem
                    clickable={!isCreateModeActive}
                    onClick={() => {
                        if (!isDisabled) {
                            setIsCreateModeActive(true);
                        }
                    }}
                    isDisabled={isDisabled}
                />
                {renderContent()}
            </StyledAddToListDropdownContent>
        </StyledAddToListDropdown>
    );
};

export default AddToListDropdown;
